import Auth from '@aws-amplify/auth';
import * as awsVars from '@/config/aws-exports';
import AWSAppSyncClient from "aws-appsync";
import gql from "graphql-tag";
import * as Queries from '@/graphql/queries';
const awsconfig = awsVars.default(process.env);
const AWS = require('aws-sdk');
let S3 = require('aws-sdk/clients/s3');

// AppSync client singleton for GraphQL queries
let appSyncClient = null;
function getAppSyncClient() {
	if (!appSyncClient) {
		appSyncClient = new AWSAppSyncClient({
			url: awsconfig.aws_appsync_graphqlEndpoint,
			region: awsconfig.aws_appsync_region,
			auth: {
				type: awsconfig.aws_appsync_authenticationType,
				jwtToken: async function() {
					try {
						return (await Auth.currentSession()).getIdToken().getJwtToken();
					} catch (err) {
						// IMPORTANT: never reject here. aws-appsync-auth-link's authLink awaits
						// this function with no .catch() - a rejection leaves the request's
						// Observable never emitting next/error/complete, so the query (and this
						// thumbnail's "loading" state) hangs forever instead of falling back.
						console.error('[image-support] Failed to get JWT token for image request:', err);
						return '';
					}
				}
			},
			disableOffline: true,
			defaultOptions: {
				query: {
					fetchPolicy: 'no-cache',
					errorPolicy: 'all'
				}
			}
		});
	}
	return appSyncClient;
}

export function lazyLoadImage(el, refreshEvent) {
	const elImage = Array.from(el.children).find(
		elSub => elSub.nodeName === "IMG"
	);

	const elName = Array.from(el.children).find(
		elSub => elSub.className.indexOf("image-fallback") > -1
	);
	
	// Guard: If elImage doesn't exist yet, retry after a short delay
	// This can happen when the directive is applied before the DOM is fully ready
	if (!elImage) {
		// Check if we've already retried to prevent infinite loops
		if (!el.dataset || el.dataset.lazyRetried !== "true") {
			// Mark that we've tried once
			if (!el.dataset) {
				el.dataset = {};
			}
			el.dataset.lazyRetried = "true";
			
			// Retry once after 50ms to allow DOM to settle
			setTimeout(() => {
				lazyLoadImage(el, refreshEvent);
			}, 50);
		}
		return;
	}

	let s3_path = refreshEvent ? refreshEvent.newId : null;

	if ((refreshEvent && !elImage.dataset.url && !refreshEvent.oldId)
		|| (refreshEvent && elImage.dataset.url && elImage.dataset.url !== "none" && refreshEvent.oldId
			&& refreshEvent.oldId.replace(".png", "") !== elImage.dataset.url.replace(".png", ""))
		|| elImage.dataset.retried === "true") {
		//console.log(`DUMP: ${JSON.stringify(refreshEvent)} - URL: ${elImage.dataset.url}`);
		//console.log("REFRESH DOES NOT APPLY TO " + elImage.dataset.source);
	} else {
		if (elImage) {
			elImage.classList.remove("no-logo-url");
		}
		if (elName) {
			elName.classList.remove("fallback-active");
		}

		//console.log("Lazy Image from " + elImage.dataset.source);

		let finalUrl = null;
		if (s3_path && s3_path !== 'none' && s3_path !== 'zapa') {
			finalUrl = s3_path;
		} else if (!s3_path && elImage && elImage.dataset && elImage.dataset.url && elImage.dataset.url !== 'none' && elImage.dataset.url !== '-1') {
			finalUrl = elImage.dataset.url;
		}

		if (!finalUrl) {
			if (elImage) {
				elImage.classList.add("no-logo-url");
			}
			if (elName) {
				elName.classList.add("fallback-active");
			} else {
				//console.log("No image fallback.");
			}
		} else {
			//TODO: CHECK IF finalUrl is UUID, if so add .png
			if (/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(finalUrl)) {
				finalUrl += ".png";
			}

			if (elImage) {
				let tempCredentials = null;

				if (elImage.dataset.token) {
					let token = JSON.parse(elImage.dataset.token).Credentials;

					tempCredentials = new AWS.Credentials({
						accessKeyId: token.AccessKeyId,
						secretAccessKey: token.SecretAccessKey,
						sessionToken: token.SessionToken
					});
				}

			// Determine CloudFront domain based on bucket type
			let cloudFrontDomain = null;
			const bucketName = elImage.dataset.bucket;
			
			if (bucketName && bucketName.indexOf('thumbnail') > -1) {
				cloudFrontDomain = awsconfig.aws_cloudfront_thumbnails_domain;
			} else if (bucketName && bucketName.indexOf('logo') > -1) {
				cloudFrontDomain = awsconfig.aws_cloudfront_logos_domain;
			} else if (bucketName && (bucketName.indexOf('filestorage') > -1 || bucketName.indexOf('storage') > -1)) {
				cloudFrontDomain = awsconfig.aws_cloudfront_files_domain;
			}

			// Debug logging for localhost development
			if (window.location.hostname === 'localhost') {
				console.log('[image-support] Loading image:', {
					bucket: bucketName,
					url: finalUrl,
					cloudFrontDomain: cloudFrontDomain,
					isPublic: elImage.dataset.source && elImage.dataset.source.indexOf("public_") === 0
				});
				
				if (!cloudFrontDomain) {
					console.warn('[image-support] CloudFront domain not configured for bucket:', bucketName);
					console.warn('[image-support] Available CloudFront domains:', {
						thumbnails: awsconfig.aws_cloudfront_thumbnails_domain,
						logos: awsconfig.aws_cloudfront_logos_domain,
						files: awsconfig.aws_cloudfront_files_domain
					});
				}
			}

			if (elImage.dataset.source && elImage.dataset.source.indexOf("public_") === 0) {
				//download image without credentials - use CloudFront if available
				//console.log("Downloading public lazy image.");
				
				let imageUrl;
				if (cloudFrontDomain) {
					// Use CloudFront URL for better performance and security
					imageUrl = `https://${cloudFrontDomain}/${finalUrl}`;
					if (window.location.hostname === 'localhost') {
						console.log('[image-support] Public CloudFront URL:', imageUrl);
					}
				} else {
					// Fallback to direct S3 URL if CloudFront not configured
					imageUrl = `https://s3.amazonaws.com/${elImage.dataset.bucket}/${finalUrl}`;
					if (window.location.hostname === 'localhost') {
						console.log('[image-support] Public S3 fallback URL:', imageUrl);
					}
				}
				
				elImage.src = imageUrl;
				elImage.onerror = function() {
					if (window.location.hostname === 'localhost') {
						console.error('[image-support] Public image failed to load:', imageUrl);
					}
					if (elName) {
						elName.classList.add("fallback-active");
					}
				};
				elImage.onload = function() {
					if (window.location.hostname === 'localhost') {
						console.log('[image-support] Public image loaded successfully:', imageUrl);
					}
					elImage.classList.remove("loading");
					el.classList.add("loaded");
					if (elName) {
						elName.classList.remove("fallback-active");
					}
				};
			} else {
				// For authenticated images with OAC (Origin Access Control):
				// OAC handles authentication between CloudFront and S3, but viewer access
				// to CloudFront requires signed URLs for security
				if (cloudFrontDomain && bucketName && bucketName.indexOf('thumbnail') > -1) {
					// Thumbnails need signed URLs - call getSignedThumbnailUrl GraphQL query
					if (window.location.hostname === 'localhost') {
						console.log('[image-support] Requesting signed URL for thumbnail:', finalUrl);
					}
					
					const client = getAppSyncClient();
					client.query({
						query: gql(Queries.getSignedThumbnailUrl),
						variables: {
							thumbnail_path: finalUrl
						}
					}).then(result => {
						if (result && result.data && result.data.getSignedThumbnailUrl) {
							const signedUrl = result.data.getSignedThumbnailUrl;
							if (window.location.hostname === 'localhost') {
								console.log('[image-support] Got signed thumbnail URL');
							}
							elImage.src = signedUrl;
							elImage.onload = function() {
								if (window.location.hostname === 'localhost') {
									console.log('[image-support] Signed thumbnail loaded successfully');
								}
								elImage.classList.remove("loading");
								el.classList.add("loaded");
								if (elName) {
									elName.classList.remove("fallback-active");
								}
							};
							elImage.onerror = function() {
								if (window.location.hostname === 'localhost') {
									console.error('[image-support] Signed thumbnail failed to load');
								}
								if (elName) {
									elName.classList.add("fallback-active");
								}
							};
						} else {
							if (window.location.hostname === 'localhost') {
								console.error('[image-support] Failed to get signed thumbnail URL');
							}
							if (elName) {
								elName.classList.add("fallback-active");
							}
						}
					}).catch(error => {
						if (window.location.hostname === 'localhost') {
							console.error('[image-support] Error getting signed thumbnail URL:', error);
						}
						if (elName) {
							elName.classList.add("fallback-active");
						}
					});
				} else if (cloudFrontDomain) {
					// Logos and other authenticated images - these may work with direct CloudFront URLs
					// depending on your CloudFront distribution configuration
					let imageUrl = `https://${cloudFrontDomain}/${finalUrl}`;
					
					if (window.location.hostname === 'localhost') {
						console.log('[image-support] CloudFront URL (may require signed URL):', imageUrl);
					}
					
					elImage.src = imageUrl;
					elImage.onerror = function() {
						if (window.location.hostname === 'localhost') {
							console.error('[image-support] CloudFront image failed to load:', imageUrl);
							console.error('[image-support] This may require signed URLs. Check CloudFront OAC configuration.');
						}
						if (elName) {
							elName.classList.add("fallback-active");
						}
					};
					elImage.onload = function() {
						if (window.location.hostname === 'localhost') {
							console.log('[image-support] CloudFront image loaded successfully');
						}
						elImage.classList.remove("loading");
						el.classList.add("loaded");
						if (elName) {
							elName.classList.remove("fallback-active");
						}
					};
				} else {
					// Fallback to S3 SDK for buckets without CloudFront (legacy)
					Auth.currentCredentials().then((currentCredentials) => {
						let s3 = new S3({
							credentials: tempCredentials ? tempCredentials : currentCredentials,
							region: "us-east-1",
							apiVersion: "2006-03-01",
							params: {
								Bucket: elImage.dataset.bucket
							}
						});

						s3.getObject({
							Bucket: elImage.dataset.bucket,
							Key: finalUrl,
							ResponseCacheControl: "no-cache"
						}, function (lazyGetObjectError, file) {
							if (lazyGetObjectError) {
								if (elName) {
									elName.classList.add("fallback-active");
								}

								elImage.dataset.retried = "true";

								//console.log("Lazy Get Image Error.. retry.");
								setTimeout(function() {
									s3.getObject({
										Bucket: elImage.dataset.bucket,
										Key: finalUrl,
										ResponseCacheControl: "no-cache"
									}, function(lazyGetObjectRetryError, retryFile) {
										if (lazyGetObjectRetryError) {
											//console.error("Failed on retry.", lazyGetObjectRetryError);
										} else {
											elImage.classList.remove("loading");
											el.classList.add("loaded");

											if (elName) {
												elName.classList.remove("fallback-active");
											}
											elImage.src = "data:image/png;base64," + encode(retryFile.Body);
										}
									});
								}, 10000);
							} else {
								//console.log("Downloaded lazy image!");
								el.classList.add("loaded");

								elImage.classList.remove("loading");
								if (elName) {
									elName.classList.remove("fallback-active");
								}

								elImage.src = "data:image/png;base64," + encode(file.Body);
							}
						});
					}).catch((reason) => {
						console.error("No Auth for Lazy Image Reason: " + JSON.stringify(reason));
					});
				}
			}

			function encode(data)
			{
				let str = data.reduce(function(a,b){ return a+String.fromCharCode(b) },'');
				return btoa(str).replace(/.{76}(?=.)/g,'$&\n');
			}
			} else {
				console.error("Lazy image dom element not found.");
			}
		}
	}
}
